import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Loading } from 'tdesign-react';
import axios from 'axios';
import { probePortalProfile } from '@/services/portal';
import { probeCurrentUser } from '@/services/user';
import { clearPortalAuth, getPortalToken } from '@/utils/auth';
import { usePortalAccount } from '@/contexts/PortalAccountContext';

interface Props {
  /** 探测确定"未登录"时渲染的内容（通常是登录页本体） */
  children: ReactNode;
}

type ProbeState = 'pending' | 'guest' | 'portal' | 'admin' | 'forbidden';

/**
 * 登录入口探测：
 * 1) 本地有门户 token → 调 /portal/me 验证 → 通过则跳 /portal；失败清本地态后落到登录页。
 * 2) 无门户 token → 调 /user/me 探测太湖身份 → 拿到非占位 userId 则跳 /accounts。
 * 3) 都不命中 → 渲染登录页。
 *
 * 同时被 "/" 与 "/portal/login" 共用：未登录时前者跳 /portal/login，后者直接渲染登录表单。
 */
const ADMIN_PLACEHOLDER_IDS = new Set(['', 'local-dev']);

const LoginEntryGate = ({ children }: Props) => {
  const [state, setState] = useState<ProbeState>('pending');
  const { setAccount } = usePortalAccount();

  useEffect(() => {
    let mounted = true;

    const probe = async () => {
      // 1) 门户 token 优先
      const portalToken = getPortalToken();
      if (portalToken) {
        try {
          const account = await probePortalProfile();
          if (!mounted) return;
          // 只写内存 Context，避免用户篡改本地存储伪造身份
          setAccount(account);
          setState('portal');
          return;
        } catch {
          if (!mounted) return;
          clearPortalAuth();
          setAccount(null);
          // 落到下一步继续探测太湖
        }
      }

      // 2) 太湖登录态
      try {
        const u = await probeCurrentUser();
        if (!mounted) return;
        if (u && !ADMIN_PLACEHOLDER_IDS.has(u.userId)) {
          setState('admin');
          return;
        }
      } catch (err) {
        if (!mounted) return;
        // 403：太湖已登录但不在 ADMIN_USERS 白名单 → 直接跳无权限页
        // 避免一直停在登录页让用户反复扫码
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          setState('forbidden');
          return;
        }
        /* 401 / 网络异常 / 太湖未配置 → 落到 guest 展示登录页 */
      }

      if (mounted) setState('guest');
    };

    void probe();
    return () => {
      mounted = false;
    };
  }, [setAccount]);

  if (state === 'pending') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loading text="登录态校验中..." />
      </div>
    );
  }

  if (state === 'portal') return <Navigate to="/portal" replace />;
  if (state === 'admin') return <Navigate to="/accounts" replace />;
  if (state === 'forbidden') return <Navigate to="/403" replace />;

  return <>{children}</>;
};

export default LoginEntryGate;
